"use client";
import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Button, message } from "antd";
import { post } from "../../../services/services";
import { ApiURL } from "../../../services/apiConstants";

const AddUserModal = ({ open, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);

  // Fetch roles
  const getRoles = async () => {
    setRolesLoading(true);
    try {
      const res = await post(ApiURL.list_roles, new FormData());
      if (res?.status === true) {
        setRoles(res.data);
      } else {
        message.error(res?.message || "Failed to fetch roles");
      }
    } catch (err) {
      message.error("Error fetching roles");
    } finally {
      setRolesLoading(false);
    }
  };

  // Initialize form when modal opens
  useEffect(() => {
    if (open) {
      getRoles();
      form.resetFields();
    }
  }, [open, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(values).forEach(([key, val]) => {
        formData.append(key, val);
      });

      const res = await post(ApiURL.user_add, formData);

      // Strict check for status: true
      if (res?.status === true) {
        message.success(res?.message || "User added successfully");
        onSuccess();
        onCancel();
        return;
      }

      // Handle cases where status is false or res is an error object
      const errorMsg = res?.error || res?.response?.data?.error || res?.message || res?.response?.data?.message;
      
      if (errorMsg && errorMsg.toLowerCase().includes("email")) {
        form.setFields([
          {
            name: "email",
            errors: ["This email is already registered"],
          },
        ]);
      } else if (errorMsg) {
        message.error(errorMsg);
      } else {
        message.error("Something went wrong while adding user");
      }
    } catch (error) {
      console.error(error);
      const apiError = error?.response?.data?.error || error?.message;
      if (apiError && apiError.toLowerCase().includes("email")) {
        form.setFields([{ name: "email", errors: ["This email is already registered"] }]);
      } else {
        message.error("Error while submitting form");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Add New User"
      open={open}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Please enter name" }]}
        >
          <Input placeholder="Enter name" autoComplete="off" />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: "Please enter email" },
            { type: "email", message: "Invalid email format" },
          ]}
        >
          <Input placeholder="Enter email" autoComplete="off" />
        </Form.Item>

        <Form.Item
          name="phone_number"
          label="Phone Number"
          rules={[
            { required: true, message: "Please enter phone number" },
            { pattern: /^[0-9]{10}$/, message: "Enter valid 10-digit number" },
          ]}
        >
          <Input 
            placeholder="Enter phone number" 
            autoComplete="off"
            maxLength={10} 
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, ""); 
              form.setFieldsValue({ phone_number: value });
            }}
          />
        </Form.Item>

        <Form.Item
          name="password"
          label="Password"
          rules={[{ required: true, message: "Please enter password" }]}
        >
          <Input.Password placeholder="Enter password" autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          name="admin_role"
          label="Role"
          rules={[{ required: true, message: "Please select a role" }]}
        >
          <Select
            placeholder="Select Role"
            loading={rolesLoading}
            notFoundContent={rolesLoading ? "Loading..." : "No roles found"}
          >
            {roles.map((r) => (
              <Select.Option key={r.key} value={r.key}>
                {r.value}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          style={{ width: "100%", marginTop: 10, backgroundColor: "#E11236", border: "none" }}
        >
          Add User
        </Button>
      </Form>
    </Modal>
  );
};

export default AddUserModal;
